/** Excel 1900 date system epoch (1899-12-30 UTC), matching ExcelJS serial conversion. */
const EXCEL_EPOCH_UTC = Date.UTC(1899, 11, 30);

export type ParseImportTemporalOptions = {
  /** Prefer clock times (HH:mm) over Excel serials / Date.parse. */
  timeOnly?: boolean;
  /** Parse full ISO datetimes as instants so local controls apply the viewer's timezone. */
  absoluteIsoDateTime?: boolean;
};

function stripWrappingQuotes(s: string): string {
  const t = s.trim();
  if (t.length >= 2) {
    const a = t[0];
    const b = t[t.length - 1];
    if ((a === '"' && b === '"') || (a === "'" && b === "'")) {
      return t.slice(1, -1).trim();
    }
  }
  return t;
}

function normalizeClockText(raw: string): string {
  return stripWrappingQuotes(String(raw).replace(/\u00a0|\u202f|\u2007|\u2009/g, " "))
    .replace(/^'+/, "")
    .replace(/[∶：﹕]/g, ":")
    .replace(/\s+/g, " ")
    .replace(/\s*(uur|hrs?|hours?)\s*$/i, "")
    .trim();
}

function dateFromTimeParts(hour: number, minute: number, second: number): Date | null {
  if (!Number.isInteger(hour) || !Number.isInteger(minute) || !Number.isInteger(second)) return null;
  if (hour > 23 || minute > 59 || second > 59) return null;
  // Local wall-clock time so TimePicker (getHours) shows what the user typed.
  return new Date(1970, 0, 1, hour, minute, second);
}

function applyAmPm(hour: number, meridiem: string | undefined): number | null {
  if (!meridiem) return hour;
  const m = meridiem.toLowerCase();
  if (m === "am") {
    if (hour === 12) return 0;
    if (hour > 12) return null;
    return hour;
  }
  if (m === "pm") {
    if (hour === 12) return 12;
    if (hour > 12) return null;
    return hour + 12;
  }
  return hour;
}

/**
 * Parse a clock time such as 15:20, 9:05:00, 3:20 PM, 15.20, or T15:20:00.000Z.
 * Full calendar datetimes are ignored here (use parseImportTemporal).
 */
export function parseClockTime(raw: unknown): Date | null {
  if (raw == null) return null;
  const str = normalizeClockText(String(raw));
  if (!str) return null;
  if (/\d{4}-\d{2}-\d{2}/.test(str)) return null;

  const clock = str.match(
    /^t?(\d{1,2})\s*[:.]\s*(\d{2})(?:\s*[:.]\s*(\d{2}))?(?:\.\d+)?(?:\s*(am|pm))?z?$/i
  );
  if (!clock) return null;

  const hour = applyAmPm(Number(clock[1]), clock[4]);
  if (hour == null) return null;
  return dateFromTimeParts(hour, Number(clock[2]), Number(clock[3] || 0));
}

function timeFromIsoDateTime(str: string): Date | null {
  const isoTime = str.match(/T(\d{1,2}):(\d{2})(?::(\d{2}))?(?:\.\d+)?(?:Z|[+-]\d{2}:?\d{2})?/i);
  if (!isoTime) return null;
  return dateFromTimeParts(Number(isoTime[1]), Number(isoTime[2]), Number(isoTime[3] || 0));
}

/**
 * Parse Excel/CSV cell text into a Date for datetime, dateonly, timeonly, and calendar import.
 * Accepts ISO datetimes, space-separated datetimes, HH:mm, Excel serials, and unix timestamps.
 */
export function parseImportTemporal(raw: unknown, options: ParseImportTemporalOptions = {}): Date | null {
  if (raw == null) return null;
  if (raw instanceof Date) {
    return Number.isNaN(raw.getTime()) ? null : raw;
  }

  const str = stripWrappingQuotes(String(raw).replace(/\u00a0|\u202f/g, " ").trim());
  if (!str) return null;

  if (options.timeOnly) {
    const clock = parseClockTime(str);
    if (clock) return clock;
    if (options.absoluteIsoDateTime && /\d{4}-\d{2}-\d{2}T/.test(str)) {
      const absoluteDate = new Date(str);
      if (!Number.isNaN(absoluteDate.getTime())) return absoluteDate;
    }
    const fromIso = timeFromIsoDateTime(str);
    if (fromIso) return fromIso;
  } else {
    const clock = parseClockTime(str);
    if (clock) return clock;
  }

  if (/^-?\d+(?:\.\d+)?$/.test(str)) {
    const n = Number(str);
    if (!Number.isFinite(n)) return null;
    if (n >= 1e12) {
      const ms = new Date(n);
      return Number.isNaN(ms.getTime()) ? null : ms;
    }
    if (n >= 1e9) {
      const sec = new Date(n * 1000);
      return Number.isNaN(sec.getTime()) ? null : sec;
    }
    if (options.timeOnly && n >= 0 && n < 1.5) {
      const frac = n % 1;
      const totalSeconds = Math.round(frac * 86400);
      const hour = Math.floor(totalSeconds / 3600);
      const minute = Math.floor((totalSeconds % 3600) / 60);
      const second = totalSeconds % 60;
      return dateFromTimeParts(hour, minute, second);
    }
    // Excel serial: whole days plus fraction of day (0–1 is time-only).
    if (n >= 0 && n < 100000) {
      const fromSerial = new Date(EXCEL_EPOCH_UTC + n * 86400000);
      return Number.isNaN(fromSerial.getTime()) ? null : fromSerial;
    }
    return null;
  }

  let normalized = str;
  if (/^\d{4}-\d{2}-\d{2}[ T]\d{1,2}:\d{2}/.test(str)) {
    normalized = str.replace(" ", "T");
  }

  const parsed = new Date(normalized);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function isDateOrTimeNumFmt(numFmt: unknown): boolean {
  if (typeof numFmt !== "string" || !numFmt.trim()) return false;
  const f = numFmt.toLowerCase();
  return /[ydhs]|am\/pm|a\/p/.test(f);
}

function isTimeOnlyNumFmt(numFmt: unknown): boolean {
  if (typeof numFmt !== "string" || !numFmt.trim()) return false;
  const f = numFmt.toLowerCase();
  if (!/[hs]|am\/pm|a\/p/.test(f)) return false;
  return !/[yd]/.test(f);
}

function formatLocalClock(d: Date): string {
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  const ss = String(d.getSeconds()).padStart(2, "0");
  return ss === "00" ? `${hh}:${mm}` : `${hh}:${mm}:${ss}`;
}

/**
 * Turn an ExcelJS cell into import text. Date cells become ISO; time-only formats
 * stay as HH:mm so timeonly fields do not need a calendar date.
 */
export function excelCellValueToString(cell: {
  value?: unknown;
  numFmt?: string;
  text?: string;
}): string {
  const v = cell?.value;
  if (v == null || v === "") return "";

  const formattedText = typeof cell.text === "string" ? cell.text.trim() : "";
  if (isTimeOnlyNumFmt(cell.numFmt) && formattedText && parseClockTime(formattedText)) {
    return formattedText;
  }

  const fromTemporal = (value: unknown): string | null => {
    if (value instanceof Date) {
      if (Number.isNaN(value.getTime())) return "";
      if (isTimeOnlyNumFmt(cell.numFmt)) return formatLocalClock(value);
      return value.toISOString();
    }
    if (typeof value === "number" && Number.isFinite(value) && isDateOrTimeNumFmt(cell.numFmt)) {
      if (isTimeOnlyNumFmt(cell.numFmt)) {
        const d = parseImportTemporal(value, { timeOnly: true });
        return d ? formatLocalClock(d) : String(value);
      }
      const d = parseImportTemporal(value);
      return d ? d.toISOString() : String(value);
    }
    return null;
  };

  const direct = fromTemporal(v);
  if (direct != null) return direct;

  if (typeof v === "string" || typeof v === "number" || typeof v === "boolean") {
    return String(v).trim();
  }

  if (typeof v === "object") {
    const rich = (v as { richText?: { text: string }[] }).richText;
    if (Array.isArray(rich)) {
      return rich.map((r) => r.text).join("").trim();
    }
    const result = (v as { result?: unknown }).result;
    if (result != null) {
      const fromResult = fromTemporal(result);
      if (fromResult != null) return fromResult;
      if (typeof result === "string" || typeof result === "number" || typeof result === "boolean") {
        return String(result).trim();
      }
      if (result instanceof Date) {
        return Number.isNaN(result.getTime()) ? "" : result.toISOString();
      }
      return String(result).trim();
    }
  }

  if (formattedText) return formattedText;
  return String(v).trim();
}
